/**
 * ARIA in HTML — "aria-required is unnecessary for elements with a required
 * attribute" (flagged on the Omnisend newsletter form input, which carries
 * both required and aria-required="true").
 *
 * Problem: native form controls already expose their required state to
 * accessibility APIs through the required attribute. Per the ARIA in HTML
 * specification, adding aria-required on top of it is redundant — it repeats
 * the same state through two channels and is flagged by accessibility
 * scanners. The right fix is to keep the native required attribute and drop
 * the redundant aria-required one.
 *
 * The flagged <input> is injected at runtime by the Omnisend app script
 * (id/class "omnisend-form-…"), which no theme markup can reach, so a
 * runtime pass is the only place that can fix it.
 *
 * How it works:
 *  - Every input/select/textarea that has BOTH required and aria-required
 *    loses the aria-required attribute. Elements that only have
 *    aria-required (custom widgets, theme forms without the native
 *    attribute) are left untouched — there aria-required is doing real work.
 *  - Live content is re-scanned through a MutationObserver (debounced via
 *    requestAnimationFrame, also watching the required/aria-required
 *    attributes), so widgets that re-render after page load are cleaned as
 *    they appear.
 *  - Manual re-scan hook for QA: window.petkitA11yRedundantAriaRequired.scan()
 *
 * @see https://www.w3.org/TR/html-aria/#el-input-email
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-required
 */
(function () {
  "use strict";

  if (window.petkitA11yRedundantAriaRequired) return; // guard against double inclusion

  var CONTROL_SELECTOR = "input[required][aria-required], select[required][aria-required], textarea[required][aria-required]";

  function strip(control) {
    if (control.namespaceURI && control.namespaceURI !== "http://www.w3.org/1999/xhtml") return;
    control.removeAttribute("aria-required");
  }

  function scan() {
    var controls = document.querySelectorAll(CONTROL_SELECTOR);
    for (var i = 0; i < controls.length; i++) {
      strip(controls[i]);
    }
  }

  function setupObserver() {
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        scan();
      });
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["required", "aria-required"]
    });
  }

  function init() {
    scan();
    setupObserver();
  }

  window.petkitA11yRedundantAriaRequired = { scan: scan };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
